import type { IButtonProps } from '@sixui/ui';
import { Button, Skeleton } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { IAppPricingTablePlan } from '~/components/organisms/AppPricingTable/plans';
import { getPathFromRegion } from '~/components/utils/Navigation/getPathFromRegion';
import { useAppRegion } from '~/hooks/useAppRegion';
import { useApi } from '~/lib/api';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { getAppSettings } from '~/lib/appSettings';

export type IUpgradeButtonProps = IContainerProps &
  IButtonProps & {
    appId: string;
    label?: string;
    plan: IAppPricingTablePlan;
  };

export const UpgradeButton: React.FC<IUpgradeButtonProps> = (props) => {
  const { sx, appId, label, plan, ...other } = props;
  const { t } = useTranslation();
  const api = useApi();
  const appSettings = getAppSettings();
  const appCurrentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({ appId });
  const featurePlanId = `${plan}FeaturePlanId`;
  const loaded =
    !!appCurrentFeaturePlanQuery.data || !appCurrentFeaturePlanQuery.isLoading;
  const canSubscribe = appCurrentFeaturePlanQuery.data?._id !== featurePlanId;
  const appRegion = useAppRegion();
  const appRegionPath = getPathFromRegion(appRegion);

  const handleUpgrade = async (): Promise<void> => {
    const successUrl = `${appSettings.environment.url}/${appRegionPath}/apps/${appId}`;
    const cancelUrl = `${appSettings.environment.url}/${appRegionPath}/apps/${appId}/settings/subscription`;

    return api.apps
      .getCheckout(appId, {
        successUrl,
        cancelUrl,
        featurePlanId,
      })
      .then((response) => {
        window.location.href = response.url;
      })
      .catch((e) => {
        // TODO: notify error
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  return (
    <Skeleton sx={sx} loaded={loaded}>
      {canSubscribe ? (
        <Button {...other} onClick={handleUpgrade}>
          {label ?? t('UpgradeButton.upgrade')}
        </Button>
      ) : (
        <Button
          {...other}
          href={appSettings.stripe.billingPortalUrl}
          target="_blank"
        >
          {t('UpgradeButton.manageSubscription')}
        </Button>
      )}
    </Skeleton>
  );
};
